import type { IAssessmentClientState } from '@repo/assessment';
import {
  type EnglishIqResponseV2Type,
  englishIqDemoV2Schema,
  englishIqItemV2Schema,
  englishIqResponseV2Schema,
  englishIqResultV2Schema,
  type IEnglishIqDemoV2,
  type IEnglishIqItemV2,
  type IEnglishIqResultV2,
} from '@repo/sdk';
import { z } from 'zod';

export type EnglishIqV2ClientItemType = IEnglishIqItemV2 & {
  renderer: IEnglishIqItemV2['type'];
};

const forbiddenClientItemKeys = new Set([
  'answer',
  'answerKey',
  'correct',
  'correctAnswer',
  'correctChoiceId',
]);

const clientItemSchema = z.custom<EnglishIqV2ClientItemType>((value) => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const record = value as Record<string, unknown>;
  if ([...forbiddenClientItemKeys].some((key) => key in record)) {
    return false;
  }
  const parsed = englishIqItemV2Schema.safeParse(value);
  return parsed.success && record.renderer === parsed.data.type;
});

const assessmentClientStateSchema = z.object({
  answeredCount: z.number().int().nonnegative(),
  attemptId: z.string().min(1).nullable(),
  currentItem: clientItemSchema.nullable(),
  currentToken: z.string().min(1).nullable(),
  error: z.string().nullable(),
  phase: z.enum(['idle', 'active', 'submitting', 'ready_to_complete', 'completed', 'error']),
  result: englishIqResultV2Schema.nullable(),
  totalCount: z.number().int().positive(),
});

const pendingAnswerSchema = z.object({
  idempotencyId: z.string().min(1),
  itemId: z.string().min(1),
  replayCount: z.number().int().min(0).max(2),
  response: englishIqResponseV2Schema,
  responseTimeMs: z.number().int().nonnegative(),
  token: z.string().min(1),
});

export interface IEnglishIqV2PendingAnswer {
  idempotencyId: string;
  itemId: string;
  replayCount: number;
  response: EnglishIqResponseV2Type;
  responseTimeMs: number;
  token: string;
}

export const englishIqV2StoredFlowSchema = z.object({
  assessment: assessmentClientStateSchema,
  demo: englishIqDemoV2Schema.nullable(),
  expiresAt: z.number().int().positive(),
  ownerSubject: z.string().min(1),
  pendingCompletionId: z.string().min(1).nullable().default(null),
  pendingResult: englishIqResultV2Schema.nullable(),
  pendingAnswer: pendingAnswerSchema.nullable().default(null),
  sourceShareId: z.string().min(1).nullable(),
  view: z.enum(['start', 'assessment', 'demo', 'result']),
});

export interface IEnglishIqV2StoredFlow {
  assessment: IAssessmentClientState<EnglishIqV2ClientItemType, IEnglishIqResultV2>;
  demo: IEnglishIqDemoV2 | null;
  expiresAt: number;
  ownerSubject: string;
  pendingCompletionId: string | null;
  pendingResult: IEnglishIqResultV2 | null;
  pendingAnswer: IEnglishIqV2PendingAnswer | null;
  sourceShareId: string | null;
  view: 'start' | 'assessment' | 'demo' | 'result';
}
